import { createFileRoute, Navigate } from '@tanstack/react-router'
import { useSession } from '@/entities/session'
import { clearLoginAttempt, redirectToLogin, sanitizeInternalPath } from '@/shared/api'
import { BrandLogo } from '@/shared/ui/brand'
import { Button } from '@/shared/ui/button'

interface LoginSearch {
  // Куда вернуться после входа (кладёт 401-guard); только внутренний путь.
  redirect?: string
  // Терминальная ошибка OIDC-callback (?error=1), если BFF вернёт с ней на /login.
  error?: boolean
}

// Вход — bare-раскладка (AppShell отдаёт /login без шелла).
export const Route = createFileRoute('/login')({
  validateSearch: (s: Record<string, unknown>): LoginSearch => ({
    ...(typeof s.redirect === 'string' && sanitizeInternalPath(s.redirect)
      ? { redirect: s.redirect }
      : {}),
    ...(s.error === '1' || s.error === 1 || s.error === true ? { error: true } : {}),
  }),
  head: () => ({ meta: [{ title: 'Вход · STIT Телемост' }] }),
  component: LoginPage,
})

function LoginPage() {
  const { redirect, error } = Route.useSearch()
  const { isAuthenticated } = useSession()

  // Уже вошедшего уводим внутрь: без явного redirect — на корень, там развилка
  // по контуру (сотрудник и заказчик попадают в разные приложения). Санитайз
  // повторно — защита от open-redirect на случай сырого search.
  if (isAuthenticated) {
    return <Navigate to={sanitizeInternalPath(redirect) ?? '/'} replace />
  }

  return (
    <main className="flex min-h-dvh items-center justify-center bg-bg-base px-4 py-12">
      <div className="w-full max-w-sm">
        <div className="mb-8 flex flex-col items-center gap-4 text-center">
          <BrandLogo className="h-7" />
          <div>
            <h1 className="t-h3 text-text-primary">Вход в STIT Телемост</h1>
            <p className="mt-1 text-sm text-text-tertiary">Видеовстречи без лишнего</p>
          </div>
        </div>

        {error && (
          <p className="mb-4 text-center text-sm text-accent">
            Не удалось войти: сессия не поднялась или вход был отклонён. Попробуйте ещё раз.
          </p>
        )}

        {/* Сюда попадают, когда автопереход на вход уже был и вернул без сессии
            (гейт страниц рвёт цикл). Клик — осознанная вторая попытка, поэтому
            флаг попытки сбрасывается. Куда идти дальше — сотруднику в Authentik
            или заказчику к форме — решает единый экран хаба, продукт по 401 не
            знает, кто перед ним. */}
        <Button
          className="w-full"
          onClick={() => {
            clearLoginAttempt()
            redirectToLogin(redirect ?? '/')
          }}
        >
          Войти
        </Button>
        <p className="mt-3 text-center text-xs text-text-tertiary">
          Единый вход экосистемы — hub.stit.tech
        </p>
      </div>
    </main>
  )
}
